import { useState } from "react";
import {
  Check, X, Star, ExternalLink, ArrowDown, ArrowUp,
  ThumbsUp, ThumbsDown, Sparkles, ShieldCheck, Heart, Ban,
  CheckCircle2, ShoppingBag, Tag, Store, Wrench, Plane, Car, Wallet, Compass,
  MapPin, Quote, Search,
} from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { trackAffiliate, voteOnRecommendation, fetchAffiliateLink, recordPurchase } from "../lib/api";
import { pickCategoryIcon } from "../lib/categoryIcons";
import { ShareMenu } from "./ShareMenu";
import { CHAT } from "../constants/testIds";

// Verdict visual mapping
const VERDICT_STYLES = {
  buy:             { dot: "bg-emerald-500", text: "text-emerald-400", bg: "bg-emerald-500/10",  border: "border-emerald-500/40", emoji: "🟢" },
  book:            { dot: "bg-emerald-500", text: "text-emerald-400", bg: "bg-emerald-500/10",  border: "border-emerald-500/40", emoji: "🟢" },
  consider:        { dot: "bg-amber-400",   text: "text-amber-300",   bg: "bg-amber-400/10",    border: "border-amber-400/40",   emoji: "🟡" },
  better_value:    { dot: "bg-amber-400",   text: "text-amber-300",   bg: "bg-amber-400/10",    border: "border-amber-400/40",   emoji: "🟡" },
  not_recommended: { dot: "bg-red-500",     text: "text-red-400",     bg: "bg-red-500/10",      border: "border-red-500/40",     emoji: "🔴" },
  pass:            { dot: "bg-red-500",     text: "text-red-400",     bg: "bg-red-500/10",      border: "border-red-500/40",     emoji: "🔴" },
};

// Tiny inline markdown renderer — handles **bold** + line breaks + numbered
// lists ("1. ", "2. ", …). Keeps zero dependencies; the AI sometimes emits
// markdown in `clarification_question` / `intro` and we want it rendered
// (not displayed as literal `**` asterisks on mobile).
export const RichText = ({ text }) => {
  if (!text) return null;
  const normalised = String(text).replace(/\s+(\d{1,2}\.\s)/g, "\n$1");
  const lines = normalised.split(/\n+/).filter((l) => l.trim());
  const renderInline = (line) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      p.startsWith("**") && p.endsWith("**") ? (
        <strong key={i} className="font-semibold text-foreground">{p.slice(2, -2)}</strong>
      ) : (
        <span key={i}>{p}</span>
      ),
    );
  };
  return (
    <div className="space-y-2">
      {lines.map((l, i) => (
        <p key={i} className="leading-relaxed">{renderInline(l)}</p>
      ))}
    </div>
  );
};

// Lookup table for the icon name returned by the backend CTA registry.
const CTA_ICON_MAP = {
  "shopping-bag": ShoppingBag,
  "store":        Store,
  "wrench":       Wrench,
  "plane":        Plane,
  "car":          Car,
  "wallet":       Wallet,
  "heart":        Heart,
  "shield":       ShieldCheck,
  "compass":      Compass,
  "tag":          Tag,
  "search":       Search,
};
const ctaIcon = (key) => CTA_ICON_MAP[key] || ExternalLink;

// ─────────────────── Global Amazon-CTA integrity rule ───────────────────
// Central rule enforced across EVERY product-card CTA on the site:
//
//   • A "Buy on Amazon" / "View on Amazon" wording + shopping-bag icon
//     is only allowed when the CTA URL is a resolved /dp/{ASIN} product
//     link.
//   • Any other URL (empty, /s?k= search, non-Amazon fallback) is
//     downgraded to "Search on Amazon" + magnifier icon so users are
//     never misled by a search page dressed as a product page.
//
// Applied wherever a CTA is rendered so the rule is enforced in ONE
// place, not patched card-by-card.
const _DP_LINK_RE = /amazon\.[^/]+\/dp\/[A-Z0-9]{10}/i;

export const isResolvedAmazonUrl = (url) => Boolean(url && _DP_LINK_RE.test(url));

export const sanitizeAmazonCta = (c) => {
  if (!c) return c;
  const url = c.url || "";
  const isAmazonCta = c.cta === "amazon" || c.retailer === "amazon";
  if (!isAmazonCta) return c;
  if (isResolvedAmazonUrl(url)) return c;
  // Downgrade: search-URL, empty URL, or any non-/dp Amazon link.
  return { ...c, label: "Search on Amazon", icon: "search" };
};

// Verified product image with graceful fallback. If we have an image URL
// from the backend (Wikipedia-verified for known products), render it with
// lazy loading + fade-in + aspect-ratio preserved. On any error or when no
// image is available, render the category icon — never a broken-image icon.
const ProductHero = ({ image, category, name }) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const hasImage = Boolean(image?.url) && !errored;
  const isBrandLogo = image?.source === "brand-logo" || image?.is_brand_logo;
  return (
    <div
      data-testid="rec-product-hero"
      className="relative w-full sm:w-52 h-44 sm:h-52 rounded-2xl overflow-hidden bg-gradient-to-br from-secondary via-card to-secondary border border-border flex items-center justify-center shrink-0 group"
    >
      {hasImage ? (
        <>
          {!loaded && (
            <div
              data-testid="rec-image-skeleton"
              className="absolute inset-0 shimmer"
            />
          )}
          <img
            src={image.url}
            alt={name}
            loading="lazy"
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => setErrored(true)}
            data-testid="rec-product-image"
            className={`${
              isBrandLogo ? "w-28 h-28 mx-auto" : "w-full h-full"
            } object-contain transition-all duration-700 group-hover:scale-105 ${
              loaded ? "opacity-100" : "opacity-0"
            } ${isBrandLogo ? "p-3" : ""}`}
          />
          {/* Subtle bottom gradient improves contrast for any label overlays */}
          <div className="absolute inset-0 hero-overlay pointer-events-none" />
          {isBrandLogo && loaded && (
            <div className="absolute bottom-2 left-0 right-0 text-center text-[10px] uppercase tracking-widest text-muted-foreground">
              {image.candidate_title}
            </div>
          )}
        </>
      ) : (
        <CategoryIconTile category={category} name={name} embedded />
      )}
      {/* AI-verified badge sits in the top-left corner of every hero */}
      <div className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-background/80 backdrop-blur-sm border border-border text-[10px] font-medium text-foreground/80">
        <Sparkles className="w-2.5 h-2.5 text-emerald-500" />
        AI pick
      </div>
    </div>
  );
};

// Hotel / attraction / restaurant / product details strip. Only renders
// when relevant fields exist. For Amazon-only V1 the common fields are
// rating + review_count + brand.
const ExtrasStrip = ({ best }) => {
  if (!best) return null;
  const extras = best.extras || {};
  const items = [];
  if (best.rating != null) {
    const reviews = best.review_count
      ? ` · ${Number(best.review_count).toLocaleString("en-GB")} reviews`
      : "";
    items.push({ k: "Rating", v: `${best.rating}/5${reviews}`, icon: Star });
  }
  if (extras.brand) items.push({ k: "Brand", v: extras.brand, icon: Tag });
  if (extras.release_year) items.push({ k: "Released", v: extras.release_year });
  // Legacy fields kept so old hotel/attraction records still render
  if (extras.address || extras.location) items.push({ k: "Location", v: extras.address || extras.location });
  if (extras.review_score != null) {
    const src = extras.review_source ? ` (${extras.review_source})` : "";
    items.push({ k: "Review", v: `${extras.review_score}/10${src}` });
  }
  if (Array.isArray(extras.room_types) && extras.room_types.length)
    items.push({ k: "Room types", v: extras.room_types.join(" · ") });
  if (extras.duration) items.push({ k: "Duration", v: extras.duration });
  if (extras.cuisine) items.push({ k: "Cuisine", v: extras.cuisine });
  if (!items.length) return null;
  return (
    <dl
      data-testid="rec-extras"
      className="flex flex-wrap gap-x-4 gap-y-1.5 mb-4 text-xs sm:text-sm"
    >
      {items.map(({ k, v, icon: Icon }) => (
        <div key={k} className="flex items-center gap-1.5 min-w-0">
          {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
          <dt className="sr-only">{k}</dt>
          <dd className="text-foreground/80 truncate" title={String(v)}>{v}</dd>
        </div>
      ))}
    </dl>
  );
};

// Format a price using the backend-provided pricing model. Falls back to
// the legacy "£X approx UK price" if pricing data is missing (old shares).
const formatPrice = (value, pricing) => {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return null;
  const isRate = pricing?.unit === "rate";
  const prefix = pricing?.prefix ?? "£";
  const suffix = pricing?.suffix ?? "";
  const shown = isRate || num < 20
    ? num.toLocaleString("en-GB", { maximumFractionDigits: 2 })
    : Math.round(num).toLocaleString("en-GB");
  return `${prefix}${shown}${suffix}`;
};

const Pros = ({ items = [] }) => (
  <div className="space-y-2.5">
    {items.map((p, i) => (
      <div key={i} className="flex items-start gap-2.5">
        <div className="mt-0.5 w-5 h-5 rounded-md bg-emerald-500/15 flex items-center justify-center shrink-0">
          <Check className="w-3 h-3 text-emerald-500" strokeWidth={3} />
        </div>
        <span className="text-sm text-foreground/90 leading-relaxed">{p}</span>
      </div>
    ))}
  </div>
);

const Cons = ({ items = [] }) => (
  <div className="space-y-2.5">
    {items.map((p, i) => (
      <div key={i} className="flex items-start gap-2.5">
        <div className="mt-0.5 w-5 h-5 rounded-md bg-red-500/15 flex items-center justify-center shrink-0">
          <X className="w-3 h-3 text-red-400" strokeWidth={3} />
        </div>
        <span className="text-sm text-foreground/90 leading-relaxed">{p}</span>
      </div>
    ))}
  </div>
);

const scoreColor = (s) => (s >= 8 ? "text-emerald-500" : s >= 5 ? "text-amber-400" : "text-red-400");
const scoreRingColor = (s) => (s >= 8 ? "stroke-emerald-500" : s >= 5 ? "stroke-amber-400" : "stroke-red-400");

// Compact circular value-score ring. Replaces the boxy "Value" tile —
// reads as a single confident number at a glance.
const ValueRing = ({ score = 0 }) => {
  const v = Math.max(0, Math.min(10, Number(score) || 0));
  const r = 28;
  const c = 2 * Math.PI * r;
  const offset = c - (v / 10) * c;
  return (
    <div
      className="flex flex-col items-center justify-center gap-1.5 shrink-0"
      data-testid={CHAT.valueScore}
    >
      <div className="relative w-20 h-20">
        <svg className="w-20 h-20 -rotate-90" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r={r} className="stroke-border" strokeWidth="5" fill="none" />
          <circle
            cx="32" cy="32" r={r}
            className={`${scoreRingColor(v)} transition-all duration-700`}
            strokeWidth="5"
            strokeLinecap="round"
            fill="none"
            strokeDasharray={c}
            strokeDashoffset={offset}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`font-display font-bold text-2xl leading-none ${scoreColor(v)}`}>{v}</span>
          <span className="text-[9px] text-muted-foreground mt-0.5">/ 10</span>
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Value</div>
    </div>
  );
};

const ConfidencePill = ({ value = 0 }) => {
  const v = Math.max(0, Math.min(100, value));
  // Honest calibration — only 85+ wears the full emerald colour. Anything
  // lower reads as cooler (amber / muted) so the buyer feels the AI is
  // being upfront about how sure it is.
  const color = v >= 85 ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
    : v >= 65 ? "text-amber-300 bg-amber-400/10 border-amber-400/30"
    : "text-muted-foreground bg-secondary border-border";
  const label = v >= 85 ? "very confident"
    : v >= 65 ? "confident"
    : "uncertain";
  return (
    <div
      data-testid={CHAT.confidenceBar}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-semibold ${color}`}
    >
      <ShieldCheck className="w-3 h-3" />
      {v}% {label}
    </div>
  );
};

const VerdictBanner = ({ verdict, confidence }) => {
  if (!verdict?.type) return null;
  const v = VERDICT_STYLES[verdict.type] || VERDICT_STYLES.consider;
  return (
    <div
      data-testid={CHAT.verdictBanner}
      className={`flex items-center gap-3 px-5 py-4 ${v.bg} border-b ${v.border}`}
    >
      <span className={`w-2.5 h-2.5 rounded-full ${v.dot} shrink-0 animate-pulse-dot`} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-0.5">
          Verdict
        </div>
        <div className={`font-display font-bold text-lg sm:text-xl tracking-tight ${v.text}`}>
          {v.emoji}  {verdict.label}
        </div>
      </div>
      {confidence != null && (
        <div className="hidden sm:block shrink-0">
          <ConfidencePill value={confidence} />
        </div>
      )}
    </div>
  );
};

const CategoryIconTile = ({ category, name, embedded = false }) => {
  const Icon = pickCategoryIcon(category, name);
  if (embedded) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-emerald-500/10 via-secondary to-secondary">
        <Icon className="w-14 h-14 text-emerald-500" strokeWidth={1.4} />
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-3 text-center">
          Category icon
        </div>
      </div>
    );
  }
  return (
    <div className="w-full sm:w-44 h-44 rounded-xl shrink-0 border border-border bg-gradient-to-br from-emerald-500/10 via-secondary to-secondary flex flex-col items-center justify-center gap-2">
      <Icon className="w-14 h-14 text-emerald-500" strokeWidth={1.4} />
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-3 text-center">
        Category icon
      </div>
    </div>
  );
};

// Trust badges row — sits under the product hero, reinforces credibility.
const TrustBadges = () => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-muted-foreground">
    <span className="inline-flex items-center gap-1">
      <ShieldCheck className="w-3 h-3 text-emerald-500" /> Independent
    </span>
    <span className="text-border">·</span>
    <span className="inline-flex items-center gap-1">
      <MapPin className="w-3 h-3 text-emerald-500" /> UK focused
    </span>
    <span className="text-border">·</span>
    <span className="inline-flex items-center gap-1">
      <Sparkles className="w-3 h-3 text-emerald-500" /> AI-verified
    </span>
  </div>
);

const Pill = ({ icon: Icon, color, text, testId }) => (
  <div
    data-testid={testId}
    className="flex items-start gap-2 text-sm leading-relaxed"
  >
    <Icon className={`w-3.5 h-3.5 mt-1 shrink-0 ${color}`} strokeWidth={2.5} />
    <span className="text-foreground/90">{text}</span>
  </div>
);

// AlternativeCard — every alternative product must be a fully clickable card
// with its own image, name, price, reason and Amazon CTA. This is a hard
// product requirement: count(products displayed) === count(Amazon links).
const AlternativeCard = ({ alt, pricing, category, onAmazonClick, readOnly }) => {
  const label = (alt.label || "").toLowerCase();
  const isCheap = label.includes("cheap") || label.includes("budget");
  const isPremium = label.includes("premium") || label.includes("upgrade");
  const LabelIcon = isCheap ? ArrowDown : (isPremium ? ArrowUp : Tag);
  const badgeColor = isCheap
    ? "bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/15"
    : isPremium
    ? "bg-purple-500/15 text-purple-300 hover:bg-purple-500/15"
    : "bg-secondary text-foreground/80 hover:bg-secondary";
  const altPrice = formatPrice(alt.price_gbp, pricing);
  const altName = alt.name || "Alternative";
  // Integrity gate: an alternative is only treated as a "resolved product"
  // when the backend attached a real ASIN AND the URL is a /dp/{ASIN}
  // link. Belt-and-braces so a mis-tagged row can't slip a shopping-bag
  // CTA past the guard on a search URL.
  const isResolved = Boolean(alt.asin) && isResolvedAmazonUrl(alt.amazon_url);
  const isLivePrice = alt.price_source === "amazon_live";
  const altUrl = alt.amazon_url
    || `https://www.amazon.co.uk/s?k=${encodeURIComponent(altName + " UK")}`;
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgErrored, setImgErrored] = useState(false);
  const hasImage = Boolean(alt.image?.url) && !imgErrored;
  const isBrandLogo = alt.image?.source === "brand-logo" || alt.image?.is_brand_logo;
  const FallbackIcon = pickCategoryIcon(category, altName);
  return (
    <div
      data-testid="rec-alternative-card"
      className="flex flex-col bg-card border border-border rounded-xl overflow-hidden hover:border-emerald-500/40 hover-lift transition"
    >
      {/* Image hero */}
      <div className="relative h-32 bg-gradient-to-br from-secondary via-card to-secondary border-b border-border flex items-center justify-center">
        {hasImage ? (
          <>
            {!imgLoaded && <div className="absolute inset-0 shimmer" />}
            <img
              src={alt.image.url}
              alt={altName}
              loading="lazy"
              decoding="async"
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgErrored(true)}
              className={`${isBrandLogo ? "w-20 h-20 p-2" : "w-full h-full"} object-contain transition-opacity duration-500 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
            />
          </>
        ) : (
          <FallbackIcon className="w-10 h-10 text-emerald-500/70" strokeWidth={1.4} />
        )}
        <Badge className={`absolute top-2 left-2 z-10 text-[10px] ${badgeColor}`}>
          <LabelIcon className="w-3 h-3 mr-1" />
          {alt.label || "Alternative"}
        </Badge>
        {altPrice && (
          <div
            className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-md bg-background/85 backdrop-blur-sm border border-border text-xs font-semibold text-foreground"
            title={isLivePrice ? "Live Amazon price" : "Price guide only — live price may vary on Amazon"}
          >
            {isLivePrice ? altPrice : `~${altPrice}`}
          </div>
        )}
      </div>
      {/* Body */}
      <div className="flex flex-col flex-1 p-4">
        <div className="font-semibold text-sm text-foreground mb-1 leading-snug">{altName}</div>
        {alt.reason && (
          <div className="text-xs text-muted-foreground leading-relaxed mb-3">{alt.reason}</div>
        )}
        {isResolved ? (
          <a
            href={altUrl}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={(e) => { if (readOnly) return; onAmazonClick?.(e); }}
            data-testid="alternative-amazon-link"
            className="mt-auto inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 transition glow-emerald-soft active:scale-[0.97]"
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            Buy on Amazon
            <ExternalLink className="w-3 h-3 opacity-70" />
          </a>
        ) : (
          // Not confidently resolved — a search URL only. Use a toned-down
          // CTA + magnifier icon + "Search on Amazon" label so the user
          // is never misled into thinking this is a resolved /dp product.
          <a
            href={altUrl}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={(e) => { if (readOnly) return; onAmazonClick?.(e); }}
            data-testid="alternative-amazon-search-link"
            className="mt-auto inline-flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-2.5 rounded-lg bg-secondary hover:bg-secondary/80 text-foreground/85 border border-border transition active:scale-[0.97]"
          >
            <Search className="w-3.5 h-3.5" />
            Search on Amazon
            <ExternalLink className="w-3 h-3 opacity-70" />
          </a>
        )}
      </div>
    </div>
  );
};

export const RecommendationCard = ({ data, searchId, readOnly = false }) => {
  const [voted, setVoted] = useState(null);
  const [voteCount, setVoteCount] = useState({ helpful: 0, unhelpful: 0 });
  const [bought, setBought] = useState(null);

  if (!data) return null;

  if (data.type === "clarify") {
    return (
      <Card className="p-5 sm:p-6 bg-card border-border animate-float-up" data-testid={CHAT.recommendationCard}>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-emerald-500" />
          <span className="text-xs uppercase tracking-widest text-emerald-500 font-semibold">Quick question</span>
        </div>
        <div className="text-base sm:text-lg text-foreground">
          <RichText text={data.clarification_question || data.intro} />
        </div>
      </Card>
    );
  }

  const best = data.best_recommendation || {};
  const verdict = data.verdict || { type: "consider", label: "WORTH CONSIDERING" };

  const ctas = Array.isArray(data.ctas) ? data.ctas.slice(0, 3) : [];
  const pricing = data.pricing || null;
  const formattedPrice = formatPrice(best.price_gbp, pricing);

  const onCtaClick = (ctaItem) => {
    if (!ctaItem?.url) return;
    if (!readOnly && searchId) {
      trackAffiliate({
        search_id: searchId,
        product_name: best.name,
        retailer: ctaItem.retailer,
        cta: ctaItem.cta,
        category: data.category,
      }).catch(() => {});
    }
    window.open(ctaItem.url, "_blank", "noopener,noreferrer");
  };

  const onAffiliate = async (cta, retailerFallback) => {
    if (readOnly || !searchId) return;
    try {
      const { url, retailer } = await fetchAffiliateLink(searchId, cta);
      trackAffiliate({
        search_id: searchId,
        product_name: best.name,
        retailer: retailer || retailerFallback,
        cta,
        category: data.category,
      }).catch(() => {});
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      window.open(
        `https://www.google.com/search?q=${encodeURIComponent(best.name + " UK")}`,
        "_blank",
        "noopener,noreferrer",
      );
    }
  };

  const onVote = async (helpful) => {
    if (voted || !searchId || readOnly) return;
    setVoted(helpful ? "up" : "down");
    try {
      const res = await voteOnRecommendation(searchId, helpful);
      setVoteCount({ helpful: res.helpful_votes, unhelpful: res.unhelpful_votes });
    } catch (e) {
      setVoted(null);
    }
  };

  const onPurchase = async (didBuy) => {
    if (bought !== null || !searchId || readOnly) return;
    setBought(didBuy);
    try {
      await recordPurchase(searchId, didBuy);
    } catch (e) {
      setBought(null);
    }
  };

  // Tracks each alternative click as its own affiliate event so we can
  // measure which alternative shapes get the most action.
  const onAlternativeClick = (alt) => {
    if (readOnly || !searchId) return;
    trackAffiliate({
      search_id: searchId,
      product_name: alt?.name,
      retailer: "Amazon UK",
      cta: "amazon_alternative",
      category: data.category,
    }).catch(() => {});
  };

  // The richer share popover (Native / Copy / WhatsApp / X) lives in
  // ShareMenu. Selecting any channel triggers `markShared` server-side
  // which is what flips the public-share gate on this recommendation.

  return (
    <Card
      className="overflow-hidden bg-card border-border animate-float-up"
      data-testid={CHAT.recommendationCard}
    >
      {/* Verdict banner */}
      <VerdictBanner verdict={verdict} confidence={data.confidence} />

      {/* Sub-header */}
      <div className="flex items-center justify-between px-5 py-2.5 border-b border-border bg-secondary/30">
        <div className="flex items-center gap-2">
          <Star className="w-3.5 h-3.5 text-emerald-500 fill-emerald-500" />
          <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
            Decision · {data.category || "general"}
          </span>
          {/* Show confidence pill here on mobile (it's hidden in the banner on small screens) */}
          {data.confidence != null && (
            <span className="sm:hidden ml-1">
              <ConfidencePill value={data.confidence} />
            </span>
          )}
        </div>
        {searchId && !readOnly && (
          <ShareMenu
            searchId={searchId}
            title={`${verdict?.label || "Should I Buy It?"}: ${best?.name || data.query_summary || ""}`}
            text={`Honest AI verdict on ${best?.name || data.query_summary || "this"} — value ${data.value_score ?? "?"} / 10`}
          />
        )}
      </div>

      <div className="p-5 sm:p-6">
        {/* Product hero - verified image or category icon fallback */}
        <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 mb-6">
          <ProductHero image={data.image} category={data.category} name={best.name} />
          <div className="flex-1 flex flex-col min-w-0">
            <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-1.5 text-foreground leading-tight">
              {best.name}
            </h2>
            {best.tagline && (
              <p className="text-sm text-muted-foreground mb-3 leading-relaxed">{best.tagline}</p>
            )}
            <ExtrasStrip best={best} />
            {/* Price row — clearly shown as estimated guidance, not a live
                Amazon price. We won't surface live pricing until Amazon
                PA-API access is granted. */}
            <div className="flex items-baseline gap-2 mb-2">
              {formattedPrice ? (
                <>
                  <span
                    className="font-display font-bold text-3xl text-foreground"
                    data-testid="rec-price-value"
                  >
                    ~{formattedPrice}
                  </span>
                  <span className="text-xs text-muted-foreground" data-testid="rec-price-label">
                    {pricing?.label || "Typical UK price"}
                  </span>
                </>
              ) : (
                <span
                  className="text-sm text-muted-foreground"
                  data-testid="rec-price-label"
                >
                  Price guide unavailable — check Amazon for the latest
                </span>
              )}
            </div>
            <p
              data-testid="rec-price-disclaimer"
              className="text-[11px] text-muted-foreground/80 mb-4 leading-snug"
            >
              Price guide only — live price may vary on Amazon.
            </p>
            <div className="mb-4">
              <TrustBadges />
            </div>
            {/* CTAs — first one is primary (filled emerald), rest are outline */}
            <div className="flex flex-wrap gap-2 mt-auto">
              {ctas.length > 0 ? (
                ctas.map((raw, idx) => {
                  // Global integrity rule — downgrades "View on Amazon"
                  // + shopping-bag to "Search on Amazon" + magnifier
                  // whenever the URL is not a resolved /dp/{ASIN} link.
                  const c = sanitizeAmazonCta(raw);
                  const Icon = ctaIcon(c.icon);
                  const isPrimary = idx === 0;
                  // Render as real <a href> — right-click "open in new tab"
                  // works, crawlers see the destination, and `rel='sponsored'`
                  // is the canonical signal for affiliate links per Google.
                  return (
                    <a
                      key={c.cta}
                      href={c.url || "#"}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      onClick={(e) => {
                        if (readOnly) {
                          e.preventDefault();
                          return;
                        }
                        if (!c.url) {
                          e.preventDefault();
                          return;
                        }
                        if (searchId) {
                          trackAffiliate({
                            search_id: searchId,
                            product_name: best.name,
                            retailer: c.retailer,
                            cta: c.cta,
                            category: data.category,
                          }).catch(() => {});
                        }
                      }}
                      data-testid={CHAT.affiliateBtn(c.cta)}
                      aria-disabled={readOnly}
                      className={
                        isPrimary
                          ? "inline-flex items-center gap-1.5 px-3.5 rounded-md text-sm bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold min-h-[42px] glow-emerald-soft active:scale-95 transition"
                          : "inline-flex items-center gap-1.5 px-3.5 rounded-md text-sm border border-border bg-background hover:bg-secondary hover:border-emerald-500/40 text-foreground min-h-[42px] transition"
                      }
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {c.label}
                      <ExternalLink className={`w-3 h-3 ${isPrimary ? "opacity-70" : "opacity-60"}`} />
                    </a>
                  );
                })
              ) : (
                // Legacy fallback only — for old recommendations with no
                // server-side ctas. New flows always populate `data.ctas`.
                // We still resolve through /api/affiliate/link so the tag
                // is applied centrally, but render as a button since the
                // URL is not known until the click handler resolves.
                // Global integrity rule: the "amazon" CTA can only render
                // as "View on Amazon" when we have a resolved ASIN.
                ["amazon", "best_price", "alternative"].map((cta, idx) => {
                  const isPrimary = idx === 0;
                  const hasResolvedAsin = Boolean(best?.asin);
                  const label = cta === "amazon"
                    ? (hasResolvedAsin ? "View on Amazon" : "Search on Amazon")
                    : cta === "best_price" ? "View Best Price" : "View Alternative";
                  return (
                    <Button
                      key={cta}
                      onClick={() => onAffiliate(cta, "")}
                      variant={isPrimary ? "default" : "outline"}
                      size="sm"
                      data-testid={CHAT.affiliateBtn(cta)}
                      disabled={readOnly}
                      className={
                        isPrimary
                          ? "gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold min-h-[42px] glow-emerald-soft active:scale-95 transition"
                          : "gap-1.5 border-border hover:bg-secondary hover:border-emerald-500/40 hover:text-foreground min-h-[42px]"
                      }
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      {label}
                    </Button>
                  );
                })
              )}
            </div>
            {/* Small, tasteful affiliate disclosure — sits directly beneath
                the Amazon CTA so it appears at the point of monetisation,
                not on the homepage. Keeps us CMA/PECR compliant without
                interrupting the buyer flow. */}
            <p
              data-testid="primary-cta-disclosure"
              className="text-[11px] text-muted-foreground/80 mt-2 leading-snug"
            >
              We may earn a commission if you purchase through this Amazon link, at no additional cost to you.
            </p>
          </div>
          {/* Compact value ring sits to the right on desktop, top on mobile */}
          <ValueRing score={data.value_score || 0} />
        </div>

        {/* Why this pick — quoted callout, the strongest part of the card */}
        {data.why && (
          <div className="relative mb-6 pl-4 sm:pl-5 py-1">
            <div className="absolute left-0 top-0 bottom-0 w-1 rounded-full bg-gradient-to-b from-emerald-500 to-emerald-500/30" />
            <div className="text-[11px] uppercase tracking-widest text-emerald-500 font-semibold mb-2 flex items-center gap-1.5">
              <Quote className="w-3 h-3" /> Why this is my pick
            </div>
            {/* The AI returns 2 short paragraphs separated by a blank line.
                Render each paragraph as its own <p> so the visual reads as
                two beats: "why it beats rivals" then "who it's for". */}
            <div className="space-y-3">
              {String(data.why)
                .split(/\n{2,}/)
                .map((para, i) => (
                  <p
                    key={i}
                    className="text-[15px] sm:text-base text-foreground leading-relaxed"
                  >
                    {para.trim()}
                  </p>
                ))}
            </div>
            {data.confidence_reason && (
              <p className="mt-2.5 text-xs text-muted-foreground leading-relaxed border-t border-border pt-2.5">
                <span className="text-foreground/70 font-medium">Why I&apos;m {(data.confidence ?? 0) >= 85 ? "this" : "only this"} confident:</span> {data.confidence_reason}
              </p>
            )}
          </div>
        )}

        {/* Pros/Cons grid */}
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-emerald-500/[0.04] border border-emerald-500/15 rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-widest text-emerald-500 font-semibold mb-3 flex items-center gap-1.5">
              <Check className="w-3 h-3" strokeWidth={3} /> Pros
            </div>
            <Pros items={data.pros || []} />
          </div>
          <div className="bg-red-500/[0.04] border border-red-500/15 rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-widest text-red-400 font-semibold mb-3 flex items-center gap-1.5">
              <X className="w-3 h-3" strokeWidth={3} /> Cons
            </div>
            <Cons items={data.cons || []} />
          </div>
        </div>

        {/* Best for / Avoid if */}
        {(data.best_for?.length || data.avoid_if?.length) ? (
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div className="bg-emerald-500/[0.04] border border-emerald-500/20 rounded-xl p-4" data-testid={CHAT.bestFor}>
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-emerald-400 font-semibold mb-3">
                <Heart className="w-3.5 h-3.5" />
                Best for
              </div>
              <div className="space-y-2">
                {(data.best_for || []).map((b, i) => (
                  <Pill key={i} icon={Check} color="text-emerald-500" text={b} />
                ))}
              </div>
            </div>
            <div className="bg-red-500/[0.04] border border-red-500/20 rounded-xl p-4" data-testid={CHAT.avoidIf}>
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-red-400 font-semibold mb-3">
                <Ban className="w-3.5 h-3.5" />
                Avoid if
              </div>
              <div className="space-y-2">
                {(data.avoid_if || []).map((b, i) => (
                  <Pill key={i} icon={X} color="text-red-400" text={b} />
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {/* Alternatives — each must have its own image + clickable Amazon link */}
        {data.alternatives?.length > 0 && (
          <div className="mb-6">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">
              Also consider
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {data.alternatives.map((a, i) => (
                <AlternativeCard
                  key={i}
                  alt={a}
                  pricing={pricing}
                  category={data.category}
                  onAmazonClick={() => onAlternativeClick(a)}
                  readOnly={readOnly}
                />
              ))}
            </div>
          </div>
        )}

        {/* Feedback bar */}
        {!readOnly && searchId && (
          <div className="flex flex-col gap-4 pt-5 border-t border-border">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="text-xs text-muted-foreground">Was this recommendation useful?</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onVote(true)}
                  disabled={!!voted}
                  data-testid={CHAT.voteHelpful}
                  className={`flex items-center gap-1.5 text-xs px-3 py-2.5 rounded-lg border transition disabled:cursor-not-allowed min-h-[40px] ${
                    voted === "up"
                      ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400"
                      : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`}
                >
                  <ThumbsUp className="w-3.5 h-3.5" />
                  Yes {voteCount.helpful > 0 && `· ${voteCount.helpful}`}
                </button>
                <button
                  onClick={() => onVote(false)}
                  disabled={!!voted}
                  data-testid={CHAT.voteUnhelpful}
                  className={`flex items-center gap-1.5 text-xs px-3 py-2.5 rounded-lg border transition disabled:cursor-not-allowed min-h-[40px] ${
                    voted === "down"
                      ? "bg-red-500/15 border-red-500/40 text-red-400"
                      : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`}
                >
                  <ThumbsDown className="w-3.5 h-3.5" />
                  No {voteCount.unhelpful > 0 && `· ${voteCount.unhelpful}`}
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="text-xs text-muted-foreground">
                Did you buy it?
                <span className="text-muted-foreground/60 ml-1.5">(helps us improve recommendations)</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onPurchase(true)}
                  disabled={bought !== null}
                  data-testid={CHAT.purchaseYes}
                  className={`flex items-center gap-1.5 text-xs px-3 py-2.5 rounded-lg border transition disabled:cursor-not-allowed min-h-[40px] ${
                    bought === true
                      ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400"
                      : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`}
                >
                  <ShoppingBag className="w-3.5 h-3.5" />
                  Yes, bought it
                </button>
                <button
                  onClick={() => onPurchase(false)}
                  disabled={bought !== null}
                  data-testid={CHAT.purchaseNo}
                  className={`flex items-center gap-1.5 text-xs px-3 py-2.5 rounded-lg border transition disabled:cursor-not-allowed min-h-[40px] ${
                    bought === false
                      ? "bg-red-500/15 border-red-500/40 text-red-400"
                      : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`}
                >
                  <X className="w-3.5 h-3.5" />
                  No, didn&apos;t buy
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default RecommendationCard;
